import { useEffect, useState } from 'react';
/**
 * AfterLCP — render children only after the page’s Largest Contentful Paint.
 *
 * PURPOSE
 *   Defers non-critical work (e.g., marketing/analytics tags, heavy widgets) until after LCP
 *   or first user interaction. This keeps the critical path clean and typically improves
 *   LCP/TBT/INP.
 *
 * HOW IT WORKS
 *   - Observes 'largest-contentful-paint' (buffered) via PerformanceObserver.
 *   - Falls back to a 3s timeout if LCP isn’t observed (or API unsupported).
 *   - Also unblocks on first pointer interaction to avoid delaying engaged users.
 *   - A local "settled" flag guarantees we resolve exactly once.
 *
 * BEHAVIOR & CAVEATS
 *   - Renders nothing until `ready` is true, then mounts {children}.
 *   - Client-only effect; safe during SSR (no window access on the server).
 *   - This changes *when* code runs, not *whether* it’s allowed (not a consent mechanism).
 *   - Ensure children are safe to mount once and may load network resources.
 *
 * USAGE
 *   <AfterLCP>
 *     <YourHeavyOrThirdPartyStuff />
 *   </AfterLCP>
 */
const AfterLCP = ({ children }: { children: React.ReactNode }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let settled = false;
    const done = () => {
      if (!settled) {
        settled = true;
        setReady(true);
      }
    };

    // LCP observer
    try {
      const po = new PerformanceObserver((list) => {
        const last = list.getEntries().at(-1);
        if (last) done();
      });

      po.observe({ type: 'largest-contentful-paint', buffered: true });
      // Safety net
      const t = setTimeout(done, 3000);
      window.addEventListener('pointerdown', done, { once: true });
      return () => {
        po.disconnect();
        clearTimeout(t);
      };
    } catch {
      setTimeout(done, 3000);
      return undefined;
    }
  }, []);
  return ready ? <>{children}</> : null;
};

export default AfterLCP;
